import { createClient } from '@supabase/supabase-js';

import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase 클라이언트 — 이 앱의 유일한 백엔드.
 *
 * @description 인증(Auth)·데이터(Postgres)·실시간(Realtime)을 한 시스템에서 처리한다.
 *
 *  ## 왜 하나로 합쳤나
 *  종전에는 인증이 enter-launcher-api(`api.gearplug.kr`), 데이터가 Supabase 로 갈려 있었다.
 *  두 시스템의 신원이 달라 이어붙이는 장치가 필요했고, 그 장치가 어긋나는 순간
 *  **"로그인은 됐는데 내 프로필이 없는"** 상태가 됐다(실제 발생 — 온보딩 저장 1분 뒤 신원이 바뀌며
 *  화면이 초기화됐다). 지금은 `auth.users.id` 하나가 곧 데이터 소유자라 어긋날 여지가 없다.
 *
 *  RLS 는 `auth.uid()` 기준이므로 클라이언트가 남의 행을 요청해도 서버가 막는다.
 *  세션 저장·갱신은 supabase-js 가 전담한다 — 앱이 토큰을 직접 들고 있으면 두 곳의 만료 시점이
 *  어긋나 "로그인된 것처럼 보이는데 요청은 실패"하는 상태가 생긴다.
 */

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

/**
 * 환경변수가 없으면 클라이언트를 만들지 않는다.
 * @description 빈 문자열로 `createClient` 를 호출하면 런타임 깊은 곳에서 알 수 없는 에러가 나므로,
 *  설정 누락을 **여기서 명시적으로** 드러낸다. 호출부는 반환 null 로 분기한다.
 */
export const isSupabaseEnabled = Boolean(url && key);

export const supabase: SupabaseClient | null = isSupabaseEnabled
  ? createClient(url as string, key as string, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    })
  : null;

/** 현재 세션의 사용자 id. 로그인 전이면 null. */
export async function getSessionUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

/** 세션이 있는 클라이언트를 돌려준다. 로그인 전이면 null — 호출부는 빈 결과로 처리한다. */
export async function getAuthedClient(): Promise<SupabaseClient | null> {
  if (!supabase) return null;
  const userId = await getSessionUserId();
  return userId ? supabase : null;
}
